/**
 * Aspilon FAQ search (blocks/aspilon-faq.liquid). Filters the questions on the page as the visitor types:
 * Shopify's search doesn't index metaobjects. Matching questions stay, categories without a match hide,
 * and a polite status says how many questions match. No animation.
 */
class AspilonFaqFilter extends HTMLElement {
  connectedCallback() {
    this.faq = this.closest('.aspilon-faq');
    this.input = this.querySelector('[data-faq-search]');
    this.status = this.querySelector('[data-faq-status]');
    this.input?.addEventListener('input', this.#onInput);
    // aspilon: the category list marks the chosen category (Blush pill on the FAQ board)
    this.nav = this.querySelector('.aspilon-faq__nav');
    this.nav?.addEventListener('click', this.#onNavClick);
    const links = [...(this.nav?.querySelectorAll('a') ?? [])];
    const hashLink = location.hash ? links.find((link) => link.getAttribute('href') === location.hash) : null;
    if (hashLink) this.#setCurrent(hashLink);
    // aspilon: a link to one question (#faq-q-<handle>) opens that answer and brings it into view, without animation.
    if (location.hash.startsWith('#faq-q-')) this.#openQuestion(location.hash.slice(1));
  }

  /** @param {string} id */
  #openQuestion(id) {
    const item = this.faq?.querySelector(`details[id="${CSS.escape(id)}"]`);
    if (!(item instanceof HTMLDetailsElement)) return;
    // Horizon's accordion sets each row's default state when it connects; open the answer after that.
    customElements.whenDefined('accordion-custom').then(() =>
      requestAnimationFrame(() => {
        item.open = true;
        item.scrollIntoView({ block: 'center', behavior: 'instant' });
      })
    );
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.#onInput);
    this.nav?.removeEventListener('click', this.#onNavClick);
  }

  /** @param {Event} event */
  #onNavClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a') : null;
    if (link) this.#setCurrent(link);
  };

  /** @param {Element} link */
  #setCurrent(link) {
    for (const other of this.nav?.querySelectorAll('a[aria-current]') ?? []) other.removeAttribute('aria-current');
    link.setAttribute('aria-current', 'true');
  }

  #onInput = () => {
    if (!this.faq || !(this.input instanceof HTMLInputElement)) return;

    const query = this.#normalize(this.input.value);
    let matches = 0;

    for (const group of this.faq.querySelectorAll('[data-faq-group]')) {
      let groupMatches = 0;

      for (const item of group.querySelectorAll('[data-faq-item]')) {
        const row = item.closest('accordion-custom') ?? item;
        const isMatch = !query || this.#normalize(item.textContent ?? '').includes(query);
        row.toggleAttribute('hidden', !isMatch);
        if (isMatch) groupMatches += 1;
      }

      group.toggleAttribute('hidden', groupMatches === 0);
      matches += groupMatches;
    }

    if (!this.status) return;
    if (!query) {
      this.status.textContent = '';
    } else if (matches === 0) {
      this.status.textContent = this.status.dataset.none ?? '';
    } else {
      const template = matches === 1 ? this.status.dataset.one : this.status.dataset.other;
      this.status.textContent = (template ?? '').replace('[count]', String(matches));
    }
  };

  /** @param {string} text */
  #normalize(text) {
    return text
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
}

if (!customElements.get('aspilon-faq-filter')) {
  customElements.define('aspilon-faq-filter', AspilonFaqFilter);
}
